import {
  parseWorkTypes,
  type FinishStandard,
  type RegionId,
  type VatMode,
  type WorkType,
} from "@/types/mvpEstimate";

const ONBOARDING_KEY = "builder-onboarding-v1";
const PREFS_KEY = "builder-company-prefs-v1";

export type LaborRateType = "per_m2" | "per_hour";

const REGIONS: RegionId[] = ["mazowieckie", "aglomeracja", "regionalne", "inne"];

export type CompanyPrefsV1 = {
  version: 1;
  region: RegionId;
  standard: FinishStandard;
  vatMode: VatMode;
  defaultMarginPercent: number;
  laborRateType: LaborRateType;
  laborRateValue: number;
  hourlyRate: number;
  companyName: string;
  /** Jedna linia do stopki oferty (np. tel / e-mail). */
  companyContactLine: string;
  phone?: string;
  email?: string;
  address?: string;
  nip?: string;
  /** Logo firmy — data URL (base64). */
  logoDataUrl?: string;
  defaultValidityDays?: 14 | 21 | 30;
  onboardingSkipped?: boolean;
  /** Domyślne typy prac w nowym kreatorze MVP (onboarding). */
  preferredWorkTypes?: WorkType[];
};

export const DEFAULT_COMPANY_PREFS: CompanyPrefsV1 = {
  version: 1,
  region: "regionalne",
  standard: "standard",
  vatMode: "23",
  defaultMarginPercent: 15,
  laborRateType: "per_m2",
  laborRateValue: 0,
  hourlyRate: 80,
  companyName: "",
  companyContactLine: "",
  onboardingSkipped: false,
};

function parseFinishStandard(v: unknown): FinishStandard {
  return v === "ekonomiczny" || v === "standard" || v === "premium" ? v : "standard";
}

function parseRegionId(v: unknown): RegionId {
  return REGIONS.includes(v as RegionId) ? (v as RegionId) : "regionalne";
}

function parseVatMode(v: unknown): VatMode {
  return v === "8" || v === "23" || v === "none" ? v : "23";
}

function parseLaborType(v: unknown): LaborRateType {
  return v === "per_hour" ? "per_hour" : "per_m2";
}

function parseOptionalString(v: unknown): string | undefined {
  return typeof v === "string" ? v : undefined;
}

function parseDefaultValidityDays(v: unknown): 14 | 21 | 30 | undefined {
  if (v === 14 || v === 21 || v === 30) return v;
  return undefined;
}

function parsePreferredWorkTypes(v: unknown): WorkType[] | undefined {
  if (!Array.isArray(v) || v.length === 0) return undefined;
  const parsed = parseWorkTypes(v);
  return parsed.length > 0 ? parsed : undefined;
}

/** Odczyt preferencji firmy (localStorage). Zawsze zwraca poprawny obiekt. */
export function loadCompanyPrefs(): CompanyPrefsV1 {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return { ...DEFAULT_COMPANY_PREFS };
    const parsed = JSON.parse(raw) as Partial<CompanyPrefsV1>;
    return {
      version: 1,
      region: parseRegionId(parsed.region),
      standard: parseFinishStandard(parsed.standard),
      vatMode: parseVatMode(parsed.vatMode),
      defaultMarginPercent:
        typeof parsed.defaultMarginPercent === "number" && !Number.isNaN(parsed.defaultMarginPercent)
          ? Math.min(100, Math.max(0, parsed.defaultMarginPercent))
          : DEFAULT_COMPANY_PREFS.defaultMarginPercent,
      laborRateType: parseLaborType(parsed.laborRateType),
      laborRateValue:
        typeof parsed.laborRateValue === "number" && !Number.isNaN(parsed.laborRateValue)
          ? Math.max(0, parsed.laborRateValue)
          : DEFAULT_COMPANY_PREFS.laborRateValue,
      hourlyRate:
        typeof parsed.hourlyRate === "number" && !Number.isNaN(parsed.hourlyRate)
          ? Math.max(0, parsed.hourlyRate)
          : DEFAULT_COMPANY_PREFS.hourlyRate,
      companyName: typeof parsed.companyName === "string" ? parsed.companyName : "",
      companyContactLine: typeof parsed.companyContactLine === "string" ? parsed.companyContactLine : "",
      phone: parseOptionalString(parsed.phone),
      email: parseOptionalString(parsed.email),
      address: parseOptionalString(parsed.address),
      nip: parseOptionalString(parsed.nip),
      logoDataUrl: parseOptionalString(parsed.logoDataUrl),
      defaultValidityDays: parseDefaultValidityDays(parsed.defaultValidityDays),
      onboardingSkipped:
        typeof parsed.onboardingSkipped === "boolean" ? parsed.onboardingSkipped : false,
      preferredWorkTypes: parsePreferredWorkTypes(parsed.preferredWorkTypes),
    };
  } catch {
    return { ...DEFAULT_COMPANY_PREFS };
  }
}

/** Jedna linia „Wykonawca” w ofercie — nazwa + kontakt (bez duplikatów). */
export function formatCompanyHeaderLine(p: CompanyPrefsV1): string | undefined {
  const name = p.companyName.trim();
  const contactParts = [p.companyContactLine, p.phone, p.email]
    .map((x) => (typeof x === "string" ? x.trim() : ""))
    .filter(Boolean);
  const contact = [...new Set(contactParts)].join(" · ");
  if (!name && !contact) return undefined;
  if (!contact) return name || undefined;
  if (!name) return contact;
  return `${name} · ${contact}`;
}

export function saveCompanyPrefs(prefs: CompanyPrefsV1): void {
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
  } catch {
    /* quota */
  }
}

export function completeOnboarding(skipped: boolean = false): void {
  try {
    const prefs = loadCompanyPrefs();
    saveCompanyPrefs({ ...prefs, onboardingSkipped: skipped });
    localStorage.setItem(ONBOARDING_KEY, "done");
  } catch {
    /* noop */
  }
}

export function hasCompletedOnboarding(): boolean {
  try {
    return localStorage.getItem(ONBOARDING_KEY) === "done";
  } catch {
    return false;
  }
}

/**
 * Czy pokazać first-run onboarding (tylko zalogowany user, route chroniony).
 * Wyłącznie po `completeOnboarding()` — szkice MVP w localStorage nie pomijają onboardingu.
 */
export function shouldShowOnboarding(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return localStorage.getItem(ONBOARDING_KEY) !== "done";
  } catch {
    return false;
  }
}
